/**
 * DMS 90-Day Strategy Operator — Sprint 5: Export Centre.
 *
 * Three exports:
 *   - Foundation as PDF (share with a partner, a mentor, or your future self)
 *   - Calendar as CSV (open in Sheets/Excel to slice)
 *   - Calendar as ICS (subscribe from Google/Apple/Outlook calendar)
 *
 * Downloads are triggered via authenticated fetch → blob → anchor click so the
 * session cookie/header is included (a bare <a href> would bypass auth).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Download, FileText, Table, Calendar as CalendarIcon, Loader2,
} from "lucide-react";


function inferFilename(headers, fallback) {
  const cd = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
  const m = /filename="([^"]+)"/.exec(cd);
  return m ? m[1] : fallback;
}


async function downloadExport(path, fallbackName) {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = inferFilename(res.headers, fallbackName);
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}


export default function StrategyExport() {
  const [busy, setBusy] = useState(null); // "pdf" | "csv" | "ics"

  const run = async (kind, path, fallback, label) => {
    setBusy(kind);
    try {
      await downloadExport(path, fallback);
      toast.success(`${label} downloaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || `${label} download failed`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-4xl px-5 sm:px-6 lg:px-10 py-6" data-testid="export-page">
      <Link to="/strategy" className="text-[13.5px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Strategy home
      </Link>
      <div className="dms-label">Export centre</div>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342] flex items-center gap-2">
        <Download className="w-6 h-6 text-[#1D4E89]" /> Take your strategy anywhere
      </h1>
      <p className="mt-2 text-[13.5px] text-[#64748B] max-w-xl">
        Download your approved foundation, or hand the 90-day calendar to a VA / editor / your favourite spreadsheet.
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <ExportCard
          testId="export-pdf"
          Icon={FileText}
          title="Foundation (PDF)"
          desc="A printable, brandable PDF of your 90-day objective, pillars, buckets, channels, campaign arcs, weekly themes and CTA strategy."
          hint="Best for: sharing with a partner or mentor."
          busy={busy === "pdf"}
          onClick={() => run("pdf", "/strategy/export/foundation.pdf", "strategy-foundation.pdf", "Foundation PDF")}
        />
        <ExportCard
          testId="export-csv"
          Icon={Table}
          title="Calendar (CSV)"
          desc="One row per day. Open in Google Sheets or Excel to filter, colour-code and hand off to your team."
          hint="Excel-friendly UTF-8 BOM · includes linked artifact titles."
          busy={busy === "csv"}
          onClick={() => run("csv", "/strategy/export/calendar.csv", "dms-calendar.csv", "Calendar CSV")}
        />
        <ExportCard
          testId="export-ics"
          Icon={CalendarIcon}
          title="Calendar (ICS)"
          desc="Subscribe from Google, Apple, or Outlook calendar. Each planned task becomes an all-day event with hook, angle, CTA and notes."
          hint="Best for: putting your posting plan next to your actual meetings."
          busy={busy === "ics"}
          onClick={() => run("ics", "/strategy/export/calendar.ics", "dms-calendar.ics", "Calendar ICS")}
        />
      </div>

      <div className="mt-8 text-[12px] text-[#94A3B8]">
        Exports reflect the current state of your foundation and calendar. Generate a fresh export any time — nothing is billed for downloads.
      </div>
    </div>
  );
}


function ExportCard({ testId, Icon, title, desc, hint, busy, onClick }) {
  return (
    <div className="dms-card p-5 flex flex-col" data-testid={testId}>
      <div className="w-10 h-10 rounded-md bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-3 font-heading font-bold text-[#0A2342] text-[15.5px]">{title}</div>
      <div className="mt-1 text-[13px] text-[#475569] leading-snug flex-1">{desc}</div>
      <div className="mt-2 text-[11.5px] text-[#94A3B8]">{hint}</div>
      <button
        onClick={onClick}
        disabled={busy}
        data-testid={`${testId}-btn`}
        className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-[#0A2342] text-white px-4 py-2 text-[13px] font-semibold hover:bg-[#152F5A] disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        {busy ? "Preparing…" : "Download"}
      </button>
    </div>
  );
}
